import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

export default function Contractors() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);

  const load = async (query = '') => {
    setLoading(true);
    try {
      const res = await api.get(`/api/contractors?limit=100${query ? `&q=${encodeURIComponent(query)}` : ''}`);
      setContractors(res.data.contractors || []);
    } catch { toast.error('Failed to load contractors'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openProfile = async (name) => {
    setSelected(name); setProfile(null);
    try {
      const res = await api.get(`/api/contractors/${encodeURIComponent(name)}`);
      setProfile(res.data);
    } catch { toast.error('Profile unavailable'); }
  };

  return (
    <div className="space-y-6" data-testid="contractors-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-text-primary">Contractor Intelligence</h1>
          <p className="text-sm text-text-muted mt-1">Market activity across every scanned permit — spot who is winning work in your area.</p>
        </div>
        <div className="flex gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(q)} placeholder="Search contractor…" className="input-field h-10 text-sm w-56" data-testid="contractor-search" />
          <button onClick={() => load(q)} className="btn-primary text-sm" data-testid="contractor-search-btn"><i className="fa-solid fa-magnifying-glass mr-1.5" />Search</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading…</div>
      ) : contractors.length === 0 ? (
        <div className="card-interactive p-10 text-center text-text-muted text-sm">No contractor data yet — run a permit scan to populate this.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {contractors.map((c, i) => (
            <button key={i} onClick={() => openProfile(c.name)} className="card-interactive p-5 text-left hover:shadow-lg transition-all" data-testid={`contractor-card-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <p className="font-bold text-text-primary truncate">{c.name}</p>
                  <p className="text-[11px] text-text-muted">{c.states.join(', ') || '—'} · {c.city_count} cities</p>
                </div>
                <span className="badge bg-primary/10 text-primary border border-primary/20 flex-shrink-0">{c.permit_count} permits</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-lg bg-surface-alt"><p className="text-[9px] uppercase tracking-widest text-text-muted">Avg value</p><p className="font-mono font-bold text-text-primary">{c.avg_project_value ? `$${c.avg_project_value.toLocaleString()}` : '—'}</p></div>
                <div className="p-2 rounded-lg bg-surface-alt"><p className="text-[9px] uppercase tracking-widest text-text-muted">Roofing / Solar</p><p className="font-mono font-bold text-text-primary">{c.roofing_permits} / {c.solar_permits}</p></div>
              </div>
              <p className="text-[10px] text-text-muted mt-2">Last activity: {c.last_activity ? new Date(c.last_activity).toLocaleDateString() : '—'}</p>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)} data-testid="contractor-modal">
          <div className="bg-surface rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="font-heading text-lg font-bold text-text-primary">{selected}</h3>
              <button onClick={() => setSelected(null)} className="p-2 text-text-muted hover:text-text-primary" data-testid="contractor-modal-close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {!profile ? <div className="text-center py-8 text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading…</div> : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-surface-alt text-center"><p className="text-2xl font-black text-primary">{profile.permit_count}</p><p className="text-[10px] uppercase tracking-widest text-text-muted">Permits</p></div>
                    <div className="p-3 rounded-xl bg-surface-alt text-center"><p className="text-2xl font-black text-primary">{profile.avg_project_value ? `$${profile.avg_project_value.toLocaleString()}` : '—'}</p><p className="text-[10px] uppercase tracking-widest text-text-muted">Avg value</p></div>
                    <div className="p-3 rounded-xl bg-surface-alt text-center"><p className="text-2xl font-black text-primary">{profile.total_project_value ? `$${profile.total_project_value.toLocaleString()}` : '—'}</p><p className="text-[10px] uppercase tracking-widest text-text-muted">Total value</p></div>
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-2">Recent permits</p>
                  <div className="space-y-2">
                    {profile.recent_permits.map((p, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-surface-alt">
                        <div className="min-w-0"><p className="text-sm font-semibold text-text-primary truncate">{p.address}, {p.city} {p.state}</p><p className="text-[11px] text-text-muted capitalize truncate">{(p.permit_type || '').replace(/_/g, ' ')}{p.description ? ` · ${p.description}` : ''}</p></div>
                        <div className="text-right flex-shrink-0"><p className="text-[11px] font-mono text-text-primary">{p.permit_date ? new Date(p.permit_date).toLocaleDateString() : '—'}</p>{p.valuation > 0 && <p className="text-[11px] font-bold text-primary">${Number(p.valuation).toLocaleString()}</p>}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
